import { useEffect, useRef } from "react";
import { message } from "antd";
import { useDispatch } from "react-redux";

import { fetchAllWorkspacesAction } from "../slices/workspaceSlice";
import { getPetFormulaVersionStatus } from "../api/internal_tool_endpoints";

const PIPELINE_POLL_INTERVAL_MS = 5000;
const PIPELINE_MAX_POLL_ATTEMPTS = 240; // ~20 min safety cap

/**
 * Poll a workspace's data-pipeline run to completion, surfacing progress as a
 * single updating toast. The Create Workspace and Add Market flows in
 * `WorkspaceUserActions` both kick off the same pipeline, so they share this
 * one copy of the polling, toasts, and timer cleanup.
 *
 * Returns `pollPipelineJob(idClient, versionId, marketsLabel)`. In-flight polls
 * are cleared on unmount.
 */
export function usePipelineJobPoll() {
  const dispatch = useDispatch();
  const pipelineTimersRef = useRef(new Set());

  useEffect(() => {
    const timers = pipelineTimersRef.current;
    return () => {
      timers.forEach((t) => clearInterval(t));
      timers.clear();
    };
  }, []);

  const pollPipelineJob = (idClient, versionId, marketsLabel) => {
    const key = `pipeline-${idClient}-${versionId}`;
    // Databricks run link, shown whenever the run id is known (pending or
    // failed) so the operator can watch progress or open the failed run's logs.
    const dagLink = (dagUrl, label) =>
      dagUrl ? (
        <>
          {" "}
          <a href={dagUrl} target="_blank" rel="noopener noreferrer">
            {label}
          </a>
        </>
      ) : null;
    const running = (dagUrl) => (
      <span>
        Rebuilding {marketsLabel || "configuration"} for <b>{idClient}</b> — the
        data pipeline is running; changes appear once it completes.
        {dagLink(dagUrl, "View pipeline progress")}
      </span>
    );
    message.open({ key, type: "loading", content: running(null), duration: 0 });
    let attempts = 0;
    const timer = setInterval(async () => {
      attempts += 1;
      try {
        const s = await getPetFormulaVersionStatus({
          id_client: idClient,
          version_id: versionId,
        });
        if (s.status === "done") {
          clearInterval(timer);
          pipelineTimersRef.current.delete(timer);
          message.open({
            key,
            type: "success",
            content: `Pipeline finished for ${idClient}${marketsLabel ? ` (${marketsLabel})` : ""}. Data updated.`,
            duration: 6,
          });
          dispatch(fetchAllWorkspacesAction());
        } else if (s.status === "failed") {
          clearInterval(timer);
          pipelineTimersRef.current.delete(timer);
          message.open({
            key,
            type: "error",
            content: (
              <span>
                Pipeline failed for <b>{idClient}</b>:{" "}
                {s.error_message || "unknown error"}
                {dagLink(s.dag_run_url, "View pipeline logs")}
              </span>
            ),
            // Longer than the success toast: the operator needs time to open the
            // failed run's logs (antd pauses the countdown while hovered).
            duration: 12,
          });
        } else {
          // Still pending — refresh so the DAG link appears once it's known.
          message.open({
            key,
            type: "loading",
            content: running(s.dag_run_url),
            duration: 0,
          });
          if (attempts >= PIPELINE_MAX_POLL_ATTEMPTS) {
            clearInterval(timer);
            pipelineTimersRef.current.delete(timer);
            message.open({
              key,
              type: "warning",
              content: `Still rebuilding ${idClient}. Check the Refresh Monitor later to confirm.`,
              duration: 6,
            });
          }
        }
      } catch (e) {
        // Transient poll error (network / token refresh) — keep polling.
        console.warn("pipeline-status poll error:", e);
      }
    }, PIPELINE_POLL_INTERVAL_MS);
    pipelineTimersRef.current.add(timer);
  };

  return pollPipelineJob;
}
